import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { type FC, useEffect, useMemo } from "react";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useAllEditorsGone } from "./hooks/use-all-editors-gone";
import { useOtherJustSaved } from "./hooks/use-other-just-saved";
import { useOtherStartedEditing } from "./hooks/use-other-started-editing";
import {
  type PanicSituation,
  type PanicSituationTrafficDict,
  type RoundaboutCurrentRole,
  type RoundaboutRoomUser,
  type RoundaboutTrackPendoEvent,
} from "./roundabout-markings";

// This is off until we get feedback from users that it's a problem.
// Until then, leave the user in a readonly state, but tell us via pendo.
const ENABLE_ALL_EDITORS_GONE = false;

export const RoundaboutTraffic: FC<{
  myRole: RoundaboutCurrentRole;
  setMyRole: (role: RoundaboutCurrentRole) => void;
  mySub: string;
  roomUsers: RoundaboutRoomUser[];
  reloadMoment: () => void; // Re fetch the moment form data
  indicate: () => Promise<void>; // Trigger explicit emit of current state when a dialog is closed
  trackPendoEvent: RoundaboutTrackPendoEvent;
}> = ({ roomUsers, myRole, setMyRole, mySub, reloadMoment, indicate, trackPendoEvent }) => {
  const { handleClose, handleOpen, open, data } = useDialog<
    PanicSituation & {
      name: string;
    }
  >();

  const { otherStartedEditing, onCloseOtherStartedEditing } = useOtherStartedEditing({
    roomUsers,
    myRole,
    mySub,
  });

  const { onCloseOtherJustSaved, otherJustSaved } = useOtherJustSaved({
    roomUsers,
    mySub,
  });

  const { allEditorsGone, onCloseAllEditorsGone } = useAllEditorsGone({
    myRole,
    roomUsers,
    mySub,
  });

  const panicSituationDict = useMemo<PanicSituationTrafficDict>(
    () => ({
      "all-editors-gone": {
        content: `You have automatically been made into the new Editor of this Communication.`,
        title: "All other editors have left this Communication",
        actions: [
          {
            label: "OK",
            onClick: () => {
              handleClose();
              onCloseAllEditorsGone();
              setMyRole("Editing");
              void indicate();
            },
          },
        ],
      },
      "other-just-saved": {
        content: `Would you like to replace your version with their latest changes, or continue editing? If you save, it will overwrite their version. If you update with their changes, you will lose your unsaved edits.`,
        title: `${otherJustSaved.saved ? otherJustSaved.nicknames.join(", ") : "Someone"} saved a new version of this Communication`,
        actions: [
          {
            label: "Update with their changes",
            onClick: () => {
              handleClose();
              onCloseOtherJustSaved();
              reloadMoment();
              void indicate();
            },
            variant: "outlined",
          },
          {
            label: "Continue editing",
            onClick: () => {
              handleClose();
              onCloseOtherJustSaved();
            },
            variant: "contained",
          },
        ],
      },
      "other-started-editing": {
        title: `${otherStartedEditing.started ? otherStartedEditing.nicknames.join(", ") : "Someone"} has started editing this Communication`,
        content:
          "Any changes you make can overwrite their edits, and vice-versa. Remember to save often, and talk to them to avoid any conflicts.",
        actions: [
          {
            label: "OK",
            onClick: () => {
              handleClose();
              onCloseOtherStartedEditing();
              void indicate();
            },
          },
        ],
      },
    }),
    [
      handleClose,
      onCloseAllEditorsGone,
      onCloseOtherJustSaved,
      onCloseOtherStartedEditing,
      reloadMoment,
      setMyRole,
      indicate,
      otherStartedEditing,
      otherJustSaved,
    ],
  );

  useEffect(() => {
    // Whilst a dialog is showing then ignore anyother sync event triggeres dialogs.
    //  They do not need to be stacked as the room keeps the current state for us.
    // Therefor we can retrigger the sync event with indicate and
    // re-receive the same data, after every close of an open dialog.

    if (open) {
      return;
    }

    if (otherStartedEditing.started) {
      const name = "other-started-editing";
      trackPendoEvent(name, { otherStartedEditing: otherStartedEditing.nicknames.join(", ") });
      handleOpen({ ...panicSituationDict[name], name });
    }

    if (otherJustSaved.saved) {
      const name = "other-just-saved";
      trackPendoEvent(name);
      handleOpen({ ...panicSituationDict[name], name });
    }

    if (allEditorsGone) {
      const name = "all-editors-gone";
      trackPendoEvent(name);
      if (ENABLE_ALL_EDITORS_GONE) {
        handleOpen({ ...panicSituationDict[name], name });
      }
    }
  }, [
    otherStartedEditing,
    otherJustSaved,
    allEditorsGone,
    trackPendoEvent,
    handleOpen,
    open,
    panicSituationDict,
  ]);

  return (
    <>
      <Dialog open={open && !!data}>
        <DialogTitle>{data?.title || ""}</DialogTitle>
        <DialogContent>
          <DialogContentText>{data?.content || ""}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {data?.actions.map((action, index) => (
            <Button
              data-analytics-id={`moment-editor-clash-${data.name || ""}-${action.label}`}
              key={index}
              onClick={action.onClick}
              variant={action.variant}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </>
  );
};
